// Static question definitions
// NOTE: This is pure data. Dynamic values (validation, computed props) are handled in the formStore.

import {
  empIncomeOptions,
  standardIncomeOptions,
  empIncomeHeadline,
  standardIncomeHeadline,
  empIncomeChat,
  standardIncomeChat
} from './empConfig'

const spAmountHeadline = 'Do You Have $20,000 or More in Combined Balances?'
const spAmountOptions = [
  { text: 'Yes', value: 100, className: 'sp-yes-option' }, // $30,000
  { text: 'No', value: 30 } // $12,500
]

// Default question order
export const DEFAULT_QUESTION_ORDER = [
  'amount',
  'income',
  'name',
  'email',
  'address',
  'dob',
  'phone'
]

export const SHORT_FORM_BASE_QUESTION_ORDER = [
  'amount',
  'name',
  'email',
  'phone'
]

// Question definitions
export const questionDefinitions = {
  amount: {
    id: 'amount',
    headline: 'How much debt do you have?',
    showHeadlines: true,
    showProgress: false,
    type: 'range',
    options: [
      { text: 'More than $20,000', value: 142 },
      { text: '$15,000 - $20,000', value: 58 },
      { text: '$10,000 - $15,000', value: 30 },
      { text: 'Less than $10,000', value: 2 }
    ],
    chat: "<p>Hi there! I'm Catherine, I'm here to help you with your personalized debt savings estimate.</p>",
    min: 0,
    max: 380,
    defaultValue: 62
  },

  employed: {
    id: 'employed',
    headline: 'Are you currently employed?',
    showHeadlines: false,
    showProgress: true,
    type: 'buttons',
    options: [
      { text: 'Yes', value: true },
      { text: 'No', value: false }
    ],
    chat: '<p>The best debt relief option could be different depending on your employment status</p>',
    defaultValue: null
  },

  income: {
    id: 'income',
    // Headline and options are dynamic based on EMP mode - use getIncomeConfig()
    showHeadlines: false,
    showProgress: true,
    type: 'buttons',
    defaultValue: null
  },

  name: {
    id: 'name',
    showHeadlines: false,
    showProgress: true,
    type: 'name',
    // Chat is dynamic based on debt/income - see formStore.nameChatMessage
    chat: '',
    defaultFirst: '',
    defaultLast: ''
  },

  dob: {
    id: 'dob',
    showHeadlines: false,
    showProgress: true,
    type: 'dob',
    chat: '<p>This will help us find specific solutions for you.</p>',
    defaultDay: '',
    defaultMonth: '',
    defaultYear: ''
  },

  email: {
    id: 'email',
    showHeadlines: false,
    showProgress: true,
    type: 'email',
    chat: "<p>We'll only send you valuable information related to your needs.</p>",
    defaultValue: '',
    defaultSendtoAC: true
  },

  phone: {
    id: 'phone',
    showHeadlines: false,
    showProgress: true,
    type: 'contact',
    chat: '<p style="font-weight: 600; margin-top: 9px;">What happens next?</p><ul><li>Your information will be encrypted using 256 SSL technology.</li><li>You&apos;ll be matched with a debt relief specialist who will call you to discuss the best options to get to a better financial situation.</li></ul>',
    defaultValue: ''
  },

  address: {
    id: 'address',
    showHeadlines: false,
    showProgress: true,
    type: 'address',
    chat: '<p>Additional options may apply based on where you live.</p>',
    defaultAddress: '',
    defaultZip: '',
    defaultCity: '',
    defaultState: ''
  }
}

// Get income question config based on EMP mode
export function getIncomeConfig(empMode) {
  return {
    ...questionDefinitions.income,
    headline: empMode ? empIncomeHeadline : standardIncomeHeadline,
    options: empMode ? empIncomeOptions : standardIncomeOptions,
    chat: empMode ? empIncomeChat : standardIncomeChat
  }
}

// Get amount question config based on SP mode
export function getAmountConfig(spMode) {
  if (!spMode) {
    return questionDefinitions.amount
  }

  return {
    ...questionDefinitions.amount,
    headline: spAmountHeadline,
    type: 'buttons',
    options: spAmountOptions
  }
}

// Get complete question config with dynamic values
export function getQuestionConfig(questionId, { empMode = false, spMode = false, nameChatMessage = '' } = {}) {
  const base = questionDefinitions[questionId]
  if (!base) return null

  // Handle amount specially (depends on SP mode)
  if (questionId === 'amount') {
    return getAmountConfig(spMode)
  }

  // Handle income specially (depends on EMP mode)
  if (questionId === 'income') {
    return getIncomeConfig(empMode)
  }

  // Handle name specially (dynamic chat message)
  if (questionId === 'name') {
    return {
      ...base,
      chat: nameChatMessage
    }
  }

  return base
}

// Get all question IDs
export function getAllQuestionIds() {
  return Object.keys(questionDefinitions)
}

export function getQuestionOrder({ shortForm = false, includeAddress = false } = {}) {
  if (!shortForm) {
    return [...DEFAULT_QUESTION_ORDER]
  }

  const questionOrder = [...SHORT_FORM_BASE_QUESTION_ORDER]

  if (includeAddress) {
    questionOrder.splice(questionOrder.indexOf('phone'), 0, 'address')
  }

  return questionOrder
}
